import { useMemo } from 'react';
import { useMetricsQuery } from '@/features/metrics';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { buildMetricsPalette } from '@/shared/constants/metricsPalette';
import { isMetricThresholdDisabled } from '@/shared/utils/metricsThresholds';
import { type SpacingToken } from '@/theme/tokens';
import { s } from '@/theme/tokens';
import { Grid, PanelHeadingText, Stack, Text, formatDuration, formatPercent } from '@/ui';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { ChartsReferenceLine, MuiBarChart, MuiLineChart, Skeleton, Surface } from '@/ui/primitives';
import type { MetricsScope, MetricsSeriesPoint } from '@/features/metrics';
import type { ReactElement } from 'react';

const resolveChartHeight = (): number => {
  const base = Number.parseFloat(s('3xl'));
  return base * 4;
};

const SPACING_MAP: Partial<Record<SpacingToken, string>> = {
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
};

const resolveChartSpacing = (token: SpacingToken): number => {
  const resolved = SPACING_MAP[token] ?? s('3xl');
  return Number.parseFloat(resolved);
};

const buildSeriesValues = (
  points: MetricsSeriesPoint[] | undefined,
  labels: string[],
): Array<number | null> => {
  if (!points) return labels.map(() => null);
  const map = new Map(points.map((point) => [point.label, point.value]));
  return labels.map((label) => map.get(label) ?? null);
};

const buildPaddedMax = (values: Array<number | null>, padRatio: number): number | undefined => {
  const finiteValues = values.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value),
  );
  if (finiteValues.length === 0) return undefined;
  const maxValue = finiteValues.reduce(
    (max, value) => (value > max ? value : max),
    finiteValues[0] ?? 0,
  );
  if (maxValue <= 0) return 0;
  return maxValue * (1 + padRatio);
};

const buildPaddedMaxWithThresholds = (
  values: Array<number | null>,
  padRatio: number,
  thresholdValues: number[],
): number | undefined => {
  const base = buildPaddedMax(values, padRatio);
  const thresholdMax =
    thresholdValues.length > 0
      ? thresholdValues.reduce((max, value) => (value > max ? value : max), thresholdValues[0])
      : undefined;
  if (base === undefined) return thresholdMax;
  if (thresholdMax === undefined) return base;
  return Math.max(base, thresholdMax);
};

const ChartCardSkeleton = (): ReactElement => {
  const chartHeight = resolveChartHeight();
  return (
    <Surface padding="md">
      <Stack gap="md">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="rectangular" height={chartHeight} />
      </Stack>
    </Surface>
  );
};

const renderChartSurface = (children: ReactElement): ReactElement => (
  <Surface padding="md">{children}</Surface>
);

type QualityExecutionTrendsSectionProps = {
  title: string;
  scope: MetricsScope;
  enabled?: boolean;
  refreshKey?: string | number | null;
};

const QualityExecutionTrendsSection = ({
  title,
  scope,
  enabled = true,
  refreshKey,
}: QualityExecutionTrendsSectionProps): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const layoutMode = useLayoutMode();
  const chartHeight = resolveChartHeight();
  const metricsQuery = useMetricsQuery({
    range: '30d',
    statuses: [],
    scope,
    projectId: selectedProjectId ?? null,
    enabled,
    refreshKey,
  });
  const metricsPalette = useMemo(() => buildMetricsPalette(), []);
  const thresholdGroup =
    scope === 'suite'
      ? thresholds.suites
      : scope === 'test'
        ? thresholds.tests
        : thresholds.pipelines;
  const passRateThreshold = thresholdGroup.passRate;
  const frequencyThreshold = thresholdGroup.frequency;
  const passRateThresholdEnabled = !isMetricThresholdDisabled(passRateThreshold);
  const frequencyThresholdEnabled = !isMetricThresholdDisabled(frequencyThreshold);
  const frequencyThresholdValues = frequencyThresholdEnabled
    ? [frequencyThreshold.warn, frequencyThreshold.target]
    : [];
  const metrics = metricsQuery.data;
  const labels = metrics?.metricsRangeLabels ?? [];

  const passRateSeries = useMemo(
    () => buildSeriesValues(metrics?.series.filteredPassRate, labels),
    [labels, metrics?.series.filteredPassRate],
  );
  const failedRateSeries = useMemo(
    () => buildSeriesValues(metrics?.series.filteredFailureRate, labels),
    [labels, metrics?.series.filteredFailureRate],
  );
  const erroredRateSeries = useMemo(
    () => buildSeriesValues(metrics?.series.filteredErrorRate, labels),
    [labels, metrics?.series.filteredErrorRate],
  );
  const skippedRateSeries = useMemo(
    () => buildSeriesValues(metrics?.series.filteredSkipRate, labels),
    [labels, metrics?.series.filteredSkipRate],
  );
  const frequencySeries = useMemo(
    () => buildSeriesValues(metrics?.series.executionTrendMetrics, labels),
    [labels, metrics?.series.executionTrendMetrics],
  );
  const durationSeries = useMemo(
    () => buildSeriesValues(metrics?.series.executionDurationAverage, labels),
    [labels, metrics?.series.executionDurationAverage],
  );
  const flakeSeries = useMemo(
    () => buildSeriesValues(metrics?.series.flakeScoreTrend, labels),
    [labels, metrics?.series.flakeScoreTrend],
  );
  const mttfSeries = useMemo(
    () => buildSeriesValues(metrics?.series.mttfTrend, labels),
    [labels, metrics?.series.mttfTrend],
  );
  const frequencyYAxis = useMemo(() => {
    const maxValue = buildPaddedMaxWithThresholds(frequencySeries, 0.05, frequencyThresholdValues);
    return maxValue === undefined ? undefined : [{ min: 0, max: maxValue }];
  }, [frequencySeries, frequencyThresholdValues]);
  const durationYAxis = useMemo(() => {
    const maxValue = buildPaddedMax(durationSeries, 0.05);
    return maxValue === undefined ? undefined : [{ min: 0, max: maxValue }];
  }, [durationSeries]);
  const flakeYAxis = useMemo(() => {
    const maxValue = buildPaddedMax(flakeSeries, 0.05);
    return maxValue === undefined ? undefined : [{ min: 0, max: Math.max(101, maxValue) }];
  }, [flakeSeries]);
  const mttfYAxis = useMemo(() => {
    const maxValue = buildPaddedMax(mttfSeries, 0.05);
    return maxValue === undefined ? undefined : [{ min: 0, max: maxValue }];
  }, [mttfSeries]);
  const passRateYAxis = useMemo(() => [{ min: 0, max: 100 }], []);
  const chartColumns =
    layoutMode === layoutModeTokens.compact ? '1fr' : 'repeat(2, minmax(0, 1fr))';
  const chartMargin = useMemo(
    () => ({
      left: resolveChartSpacing('2xl'),
      right: resolveChartSpacing('md'),
      top: resolveChartSpacing('md'),
      bottom: resolveChartSpacing('lg'),
    }),
    [],
  );
  const sectionGap: SpacingToken = layoutMode === layoutModeTokens.compact ? 'sm' : 'md';

  return (
    <Stack gap={sectionGap}>
      <Stack gap="sm">
        <Text role="sectionTitle">{title}</Text>
        <Text role="caption" color="textSecondary">
          Last 30d
        </Text>
      </Stack>
      <Grid columns={chartColumns} gap="md">
        {metricsQuery.isLoading ? (
          <>
            <ChartCardSkeleton />
            <ChartCardSkeleton />
            <ChartCardSkeleton />
            <ChartCardSkeleton />
            <ChartCardSkeleton />
            <ChartCardSkeleton />
          </>
        ) : (
          <>
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Success Rate</PanelHeadingText>
                <MuiLineChart
                  height={chartHeight}
                  xAxis={[{ data: labels, scaleType: 'point' }]}
                  yAxis={passRateYAxis}
                  series={[
                    {
                      id: 'passRate',
                      label: 'Success Rate',
                      data: passRateSeries,
                      color: metricsPalette.pass,
                      showMark: false,
                      valueFormatter: (value) =>
                        value === null ? '-' : `${formatPercent(value)}%`,
                    },
                  ]}
                  margin={chartMargin}
                >
                  {passRateThresholdEnabled ? (
                    <>
                      <ChartsReferenceLine
                        y={passRateThreshold.warn}
                        lineStyle={{
                          stroke: metricsPalette.skip,
                          strokeDasharray: '4 3',
                          strokeWidth: 1,
                        }}
                      />
                      <ChartsReferenceLine
                        y={passRateThreshold.target}
                        lineStyle={{
                          stroke: metricsPalette.pass,
                          strokeDasharray: '4 3',
                          strokeWidth: 1,
                        }}
                      />
                    </>
                  ) : null}
                </MuiLineChart>
              </Stack>,
            )}
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Status Mix</PanelHeadingText>
                <MuiBarChart
                  height={chartHeight}
                  xAxis={[
                    {
                      data: labels,
                      scaleType: 'band',
                      tickSize: 0,
                    },
                  ]}
                  yAxis={[
                    {
                      min: 0,
                      tickSize: 0,
                      valueFormatter: (value: number) =>
                        typeof value === 'number' &&
                        Number.isFinite(value) &&
                        Number.isInteger(value)
                          ? String(value)
                          : '',
                    },
                  ]}
                  series={[
                    {
                      id: 'passed',
                      label: 'Passed',
                      data: passRateSeries,
                      color: metricsPalette.pass,
                      stack: 'status',
                    },
                    {
                      id: 'failed',
                      label: 'Failed',
                      data: failedRateSeries,
                      color: metricsPalette.fail,
                      stack: 'status',
                    },
                    {
                      id: 'errored',
                      label: 'Errored',
                      data: erroredRateSeries,
                      color: metricsPalette.error,
                      stack: 'status',
                    },
                    {
                      id: 'skipped',
                      label: 'Skipped',
                      data: skippedRateSeries,
                      color: metricsPalette.skip,
                      stack: 'status',
                    },
                  ]}
                  margin={chartMargin}
                  sx={{
                    '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': {
                      transform: 'translateY(8px)',
                    },
                    '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
                      transform: 'translateX(-6px)',
                    },
                  }}
                />
              </Stack>,
            )}
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Run Frequency</PanelHeadingText>
                <MuiLineChart
                  height={chartHeight}
                  xAxis={[{ data: labels, scaleType: 'point' }]}
                  yAxis={frequencyYAxis}
                  series={[
                    {
                      id: 'frequency',
                      label: 'Runs',
                      data: frequencySeries,
                      color: metricsPalette.frequency,
                      showMark: false,
                    },
                  ]}
                  margin={chartMargin}
                >
                  {frequencyThresholdEnabled ? (
                    <>
                      <ChartsReferenceLine
                        y={frequencyThreshold.warn}
                        lineStyle={{
                          stroke: metricsPalette.skip,
                          strokeDasharray: '4 3',
                          strokeWidth: 1,
                        }}
                      />
                      <ChartsReferenceLine
                        y={frequencyThreshold.target}
                        lineStyle={{
                          stroke: metricsPalette.pass,
                          strokeDasharray: '4 3',
                          strokeWidth: 1,
                        }}
                      />
                    </>
                  ) : null}
                </MuiLineChart>
              </Stack>,
            )}
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Average Duration</PanelHeadingText>
                <MuiLineChart
                  height={chartHeight}
                  xAxis={[{ data: labels, scaleType: 'point' }]}
                  yAxis={durationYAxis}
                  series={[
                    {
                      id: 'duration',
                      label: 'Avg Duration',
                      data: durationSeries,
                      color: metricsPalette.duration,
                      showMark: false,
                      valueFormatter: (value) =>
                        value === null ? '-' : formatDuration(value * 1000),
                    },
                  ]}
                  margin={chartMargin}
                />
              </Stack>,
            )}
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Flake Rate</PanelHeadingText>
                <MuiLineChart
                  height={chartHeight}
                  xAxis={[{ data: labels, scaleType: 'point' }]}
                  yAxis={flakeYAxis}
                  series={[
                    {
                      id: 'flake',
                      label: 'Flake Rate',
                      data: flakeSeries,
                      color: metricsPalette.flake,
                      showMark: false,
                      valueFormatter: (value) =>
                        value === null ? '-' : `${formatPercent(value)}%`,
                    },
                  ]}
                  margin={chartMargin}
                />
              </Stack>,
            )}
            {renderChartSurface(
              <Stack gap="md">
                <PanelHeadingText>Mean Time To Fix</PanelHeadingText>
                <MuiLineChart
                  height={chartHeight}
                  xAxis={[{ data: labels, scaleType: 'point' }]}
                  yAxis={mttfYAxis}
                  series={[
                    {
                      id: 'mttf',
                      label: 'MTTF',
                      data: mttfSeries,
                      color: metricsPalette.mttf,
                      showMark: false,
                      valueFormatter: (value) =>
                        value === null ? '-' : formatDuration(value * 3600 * 1000),
                    },
                  ]}
                  margin={chartMargin}
                />
              </Stack>,
            )}
          </>
        )}
      </Grid>
    </Stack>
  );
};

export const SuiteTrendsSection = ({
  enabled = true,
  refreshKey,
}: {
  enabled?: boolean;
  refreshKey?: string | number | null;
}): ReactElement => (
  <QualityExecutionTrendsSection
    title="Suites Over Time"
    scope="suite"
    enabled={enabled}
    refreshKey={refreshKey}
  />
);

export const TestTrendsSection = ({
  enabled = true,
  refreshKey,
}: {
  enabled?: boolean;
  refreshKey?: string | number | null;
}): ReactElement => (
  <QualityExecutionTrendsSection
    title="Tests Over Time"
    scope="test"
    enabled={enabled}
    refreshKey={refreshKey}
  />
);

export const PipelineTrendsSection = ({
  enabled = true,
  refreshKey,
}: {
  enabled?: boolean;
  refreshKey?: string | number | null;
}): ReactElement => (
  <QualityExecutionTrendsSection
    title="Pipelines Over Time"
    scope="pipeline"
    enabled={enabled}
    refreshKey={refreshKey}
  />
);
